import {
  useCompany,
  useUpdateCompany,
} from '@/core/domain/enterprise/Companies/Companies.hooks';
import {
  Company,
  CompanyTerms,
  CompanyUpdateForm,
  CompanyUpdateRequest,
} from '@/core/domain/enterprise/Companies/Companies.types';
import {
  useCreateCustomTerm,
  useCustomTerms,
  useUpdateCustomTerm,
} from '@/core/domain/enterprise/Companies/CompaniesCustomTerms/CustomTerms.hooks';
import {
  CompanyInvoiceSettingsForm,
  InvoiceSettingsData,
} from '@/core/domain/invoice/settings/Settings.types';
import { CompanyReportFormValues } from '@/core/domain/reports/company/Company.types';
import {
  CompanyStockConfigurationCostItemCreateRequest,
  CompositionPriceFormInterface,
  CostItemsFieldsEnum,
} from '@/core/domain/stock/Configuration/Configuration.types';
import { useUpdateWorkorderConfigurationCompany } from '@/core/domain/workorder/WorkordersConfiguration/WorkordersConfiguration.hooks';
import {
  WorkorderConfigurationCompany,
  WorkorderConfigurationCompanyFormValues,
} from '@/core/domain/workorder/WorkordersConfiguration/WorkordersConfiguration.types';
import { useDefaultFormErrorNotification } from '@/hooks';
import { removeMask } from '@/utils/mask';
import { Form, FormInstance, message, Modal } from 'antd';
import { AxiosError } from 'axios';
import { FormChangeInfo } from 'rc-field-form/es/FormContext';
import React, {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useRouteMatch } from 'react-router-dom';
import { handlerInvoiceSettings } from './handlers/invoiceSettings';
import {
  useCreateInvoiceSettings,
  useInvoiceSettingsByCompany,
  useUpdateInvoiceSettings,
} from '@/core/domain/invoice/settings/Settings.hooks';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import Authorities from '@/types/Authority';
import { useCompanyReportConfigUpdate } from '@/core/domain/enterprise/Companies/CompaniesReportConfig/CompaniesReportConfig.hooks';
import { CouponCompanyConfig } from '@/core/domain/coupon/company/company.types';
import { useCouponCompanyConfigCreate } from '@/core/domain/coupon/company/company.hooks';
import {
  CompanySintegraDTO,
  CompanySintegraFormValues,
} from '@/core/domain/sintegra/Sintegra.types';
import {
  useCompanySintegraEdit,
  useGetCompanySintegra,
} from '@/core/domain/sintegra/Sintegra.hooks';
import { useCompanySintegraCreate } from '@/core/domain/sintegra/Sintegra.hooks';
import { useUpdateCompanyStockConfigurationFields } from '@/core/domain/stock/Configuration/Configuration.hooks';
import { removeMaskDetailList } from '@/utils/mask/removeMaskDetailList';
import { CustomTermsResponse } from '@/core/domain/enterprise/Companies/CompaniesCustomTerms/CustomTerms.types';
import { removeCpfCnpjMask } from '@/utils/mask/removeMask';

type Props = PropsWithChildren<unknown>;
type Forms = {
  companyGeneral?: CompanyUpdateForm;
  companyTerms?: CompanyTerms;
  companyCompositionPrice?: CompositionPriceFormInterface;
  companyBudget?: WorkorderConfigurationCompanyFormValues;
  companyInvoice?: CompanyInvoiceSettingsForm;
  companySintegra?: CompanySintegraFormValues;
  companyReport?: CompanyReportFormValues;
  companyCoupon?: CouponCompanyConfig;
};

type InstanceForms = {
  companyGeneral?: FormInstance<CompanyUpdateForm>;
  companyTerms?: FormInstance<CompanyTerms>;
  companyCompositionPrice?: FormInstance<CompositionPriceFormInterface>;
  companyBudget?: FormInstance<WorkorderConfigurationCompanyFormValues>;
  companyInvoice?: FormInstance<CompanyInvoiceSettingsForm>;
  companySettingsInvoice?: FormInstance<CompanyInvoiceSettingsForm>;
  companySintegra?: FormInstance<CompanySintegraFormValues>;
  companyReport?: FormInstance<CompanyReportFormValues>;
  companyCoupon?: FormInstance<CouponCompanyConfig>;
};

type FormKeys = keyof InstanceForms;

type CompanyFormContextProps = {
  isLoading: boolean;
  formValues: Forms;
  companyId: number;
  disabled: boolean;
  handleSubmit: () => void;
  forms: InstanceForms;
  customTermsData?: CustomTermsResponse;
  sintegraData?: CompanySintegraDTO;
  company?: Company;
  invoiceSettings?: InvoiceSettingsData;
  useInternalInvoiceApiNfe?: boolean;
  useInternalInvoiceApiNfc?: boolean;
  useInternalInvoiceApiNfs?: boolean;
};

const CompanyFormContext = createContext({} as CompanyFormContextProps);
export const useCompanyFormContext = () => useContext(CompanyFormContext);

const CompanyFormProvider: React.FC<Props> = ({ children }) => {
  const { sendFormErrorNotification } = useDefaultFormErrorNotification();
  const { params } = useRouteMatch<{ companyId: string }>();
  const companyId = Number(params.companyId);

  const { data: company } = useCompany(companyId);

  const { data: invoiceSettings } = useInvoiceSettingsByCompany(companyId);

  const useInternalInvoiceApiNfe = invoiceSettings?.useInternalInvoiceApiNfe;
  const useInternalInvoiceApiNfc = invoiceSettings?.useInternalInvoiceApiNfc;
  const useInternalInvoiceApiNfs = invoiceSettings?.useInternalInvoiceApiNfs;

  const { data: customTermsData, mutateAsync: customTermsMutateAsync } =
    useCustomTerms();

  const { data: sintegraData, mutateAsync } = useGetCompanySintegra();

  const [formValues, setFormValues] = useState<Forms>({});
  const [forms, setForms] = useState<InstanceForms>({});

  const [isLoading, setIsLoading] = useState(false);

  const generalCompanyUpdate = useUpdateCompany();
  const companyBudgetUpdate = useUpdateWorkorderConfigurationCompany();
  const updateCompanyStockConfigurationFields =
    useUpdateCompanyStockConfigurationFields();
  const customTermCreate = useCreateCustomTerm();
  const customTermUpdate = useUpdateCustomTerm();

  const createInvoiceSettings = useCreateInvoiceSettings();
  const updateInvoiceSettings = useUpdateInvoiceSettings();

  const updateConfig = useCompanyReportConfigUpdate();

  const createCouponConfigCompany = useCouponCompanyConfigCreate();

  const companySintegraCreate = useCompanySintegraCreate();
  const companySintegraEdit = useCompanySintegraEdit();

  useEffect(() => {
    if (companyId) {
      mutateAsync(Number(companyId));
      customTermsMutateAsync(Number(companyId));
    }
  }, [companyId]);

  const disabled = !Object.keys(formValues).some((key) => !!key);

  function handleFormChange(name: string, info: FormChangeInfo) {
    if (name && info?.forms[name]) {
      setForms((state) => {
        if (!state[name as FormKeys]) {
          return { ...state, [name]: info.forms[name] };
        }

        return state;
      });

      setFormValues((state) => ({
        ...state,
        [name]: info.forms[name]?.getFieldsValue(),
      }));
    }
  }

  async function handleSubmit() {
    setIsLoading(true);
    try {
      const companyData = formValues.companyGeneral || company;
      const companyValues = formValues.companyGeneral;

      if (companyValues) {
        const obj: CompanyUpdateRequest = {
          ...companyValues,
          logo: company?.logo || null,
          contactList: removeMaskDetailList(
            companyValues.contactList.map((cur) => ({
              ...cur,
              active: true,
            })),
          ),
          id: companyId,
          cnpj: removeCpfCnpjMask(companyValues.cnpj),
          companyAddress: {
            ...companyValues.companyAddress,
            zip: removeMask(companyValues.companyAddress.zip),
          },
          pecaAiContactPhone: removeMask(companyValues.pecaAiContactPhone),
        };
        await generalCompanyUpdate.mutateAsync(obj);
        setFormValues((prev) => ({ ...prev, companyGeneral: undefined }));
      }

      if (!companyData) {
        return message.warning(
          'Não foi possível recuperar os dados da empresa atual.',
        );
      }

      if (formValues.companyBudget) {
        const values = formValues.companyBudget;
        const obj: WorkorderConfigurationCompany = {
          ...values,
          company: {
            id: companyId,
          },
          profitMargin: values.profitMargin / 100,
          maxPartDiscount: values.maxPartDiscount / 100,
          maxServiceDiscount: values.maxServiceDiscount / 100,
          partPaint: values.partPaint
            ? {
                ...values.partPaint,
                costPerHour: values.partPaint.costPerHour / 100,
              }
            : undefined,
          partRecuperation: values.partRecuperation
            ? {
                ...values.partRecuperation,
                costPerHour: values.partRecuperation.costPerHour / 100,
              }
            : undefined,
          partChange: values.partChange
            ? {
                ...values.partChange,
                costPerHour: values.partChange.costPerHour / 100,
              }
            : undefined,
          ciliaSubAreas: values?.ciliaSubAreas?.filter(
            (item) => !!item.subAreaId,
          ),
          isInstallmentFree: values.isInstallmentFree
            ? JSON.parse(values.isInstallmentFree)
            : undefined,
        };
        await companyBudgetUpdate.mutateAsync({ companyId: companyId, obj });
      }

      if (formValues.companyCompositionPrice) {
        const costItems = [
          {
            field: CostItemsFieldsEnum.ipi,
            active: formValues.companyCompositionPrice.ipi,
          },
          {
            field: CostItemsFieldsEnum.taxReplacement,
            active: formValues.companyCompositionPrice.tax_replacement,
          },
          {
            field: CostItemsFieldsEnum.freight,
            active: formValues.companyCompositionPrice.freight,
          },
          {
            field: CostItemsFieldsEnum.accessory,
            active: formValues.companyCompositionPrice.accessory,
          },
          {
            field: CostItemsFieldsEnum.fixedCost,
            active: formValues.companyCompositionPrice.fixed_cost,
          },
          {
            field: CostItemsFieldsEnum.icms,
            active: formValues.companyCompositionPrice.icms,
          },
          {
            field: CostItemsFieldsEnum.operational,
            active: formValues.companyCompositionPrice.operational,
          },
          {
            field: CostItemsFieldsEnum.profitMargin,
            active: formValues.companyCompositionPrice.profit_margin,
          },
        ];

        const req: CompanyStockConfigurationCostItemCreateRequest = {
          companyId: companyId,
          costItems,
        };

        await updateCompanyStockConfigurationFields.mutateAsync(req);
      }

      if (formValues.companyTerms) {
        try {
          if (
            hasPermissions([
              Authorities.REGISTER_COMPANY_CUSTOM_TERMS_CREATE,
              Authorities.REGISTER_COMPANY_CUSTOM_TERMS_DELETE,
              Authorities.REGISTER_COMPANY_CUSTOM_TERMS_UPDATE,
            ])
          ) {
            if (!customTermsData) {
              await customTermCreate.mutateAsync({
                companyId: companyId,
                ...formValues.companyTerms,
              });
              customTermsMutateAsync(Number(companyId));
            } else {
              await customTermUpdate.mutateAsync({
                companyId: companyId,
                ...formValues.companyTerms,
              });
              customTermsMutateAsync(Number(companyId));
            }
          }
        } catch {}
      }

      if (formValues.companyInvoice && companyData) {
        const handleInvoiceData = handlerInvoiceSettings(
          formValues.companyInvoice,
        );

        const data: InvoiceSettingsData = {
          ...handleInvoiceData,
          companyId: companyData.id,
        };

        if (data.id) {
          await updateInvoiceSettings.mutateAsync(data);
        } else {
          await createInvoiceSettings.mutateAsync(data);
        }

        if (data.configNfs?.activeNfseNacional) {
          Modal.confirm({
            title: 'NFS Nacional habilitada',
            content: 'Séries não numéricas serão excluídas automaticamente',
            okButtonProps: { style: { display: 'none' } },
            cancelText: 'Fechar',
          });
        }
      }

      if (formValues.companyReport) {
        await updateConfig.mutateAsync({
          companyId,
          ...formValues.companyReport,
        });
      }

      if (formValues.companyCoupon) {
        await createCouponConfigCompany.mutateAsync({
          companyId,
          ...formValues.companyCoupon,
        });
      }

      if (formValues.companySintegra) {
        const companySintegraFormValues =
          await forms.companySintegra?.validateFields();

        const obj = {
          ...companySintegraFormValues,
          responsibleContactPhone: removeMask(
            formValues.companySintegra.responsibleContactPhone,
          ),
          companyId,
        };

        if (!sintegraData) {
          await companySintegraCreate.mutateAsync(obj);
        } else {
          await companySintegraEdit.mutateAsync(obj);
        }
      }
    } catch (error: unknown) {
      const err = error as AxiosError;
      if (!err.isAxiosError) {
        sendFormErrorNotification(error);
      }
    } finally {
      setFormValues({});
      setIsLoading(false);
    }
  }

  return (
    <CompanyFormContext.Provider
      value={{
        isLoading,
        formValues,
        companyId,
        disabled,
        handleSubmit,
        forms,
        customTermsData,
        sintegraData,
        company,
        invoiceSettings,
        useInternalInvoiceApiNfe,
        useInternalInvoiceApiNfc,
        useInternalInvoiceApiNfs,
      }}
    >
      <Form.Provider
        onFormFinish={handleSubmit}
        onFormChange={handleFormChange}
      >
        {children}
      </Form.Provider>
    </CompanyFormContext.Provider>
  );
};

export default CompanyFormProvider;
